<template>
  <div class="rw-page">
    <div class="rw-toolbar">
      <div class="rw-toolbar__inner">
        <v-select
          v-model="filters.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          :disabled="loading"
          class="rw-field rw-field--wide"
          @change="loadWorking"
        />

        <v-select
          v-model="filters.batch"
          :items="batchItems"
          item-text="label"
          item-value="value"
          label="Batch"
          outlined
          dense
          hide-details
          :disabled="loading || !filters.partner_id"
          class="rw-field"
          @change="loadWorking"
        />

        <v-menu
          v-model="fromMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.from_date"
              label="From"
              prepend-inner-icon="mdi-calendar-start"
              readonly
              outlined
              dense
              hide-details
              :disabled="loading || !filters.partner_id"
              class="rw-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.from_date"
            no-title
            @input="(fromMenu = false), loadWorking()"
          />
        </v-menu>

        <v-menu
          v-model="toMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.to_date"
              label="To"
              prepend-inner-icon="mdi-calendar-end"
              readonly
              outlined
              dense
              hide-details
              :disabled="loading || !filters.partner_id"
              class="rw-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.to_date"
            no-title
            @input="(toMenu = false), loadWorking()"
          />
        </v-menu>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <div v-on="on">
              <v-checkbox
                v-model="filters.include_prior_unbilled"
                label="Include earlier unbilled"
                dense
                hide-details
                class="rw-check"
                :disabled="loading || !filters.batch"
                @change="loadWorking"
              />
            </div>
          </template>
          <span>
            Also pull in collections of this intake from before the window that
            no raised invoice has claimed — advance fees paid before the batch
            started.
          </span>
        </v-tooltip>

        <v-spacer />

        <v-btn
          v-if="working && working.totals.collection_count"
          small
          outlined
          color="primary"
          class="ml-2"
          :disabled="loading"
          @click="downloadWorking"
        >
          <v-icon small left>mdi-download-outline</v-icon>
          Export
        </v-btn>

        <v-btn
          v-if="working && working.totals.collection_count"
          small
          depressed
          color="primary"
          class="ml-2"
          :disabled="loading"
          @click="invoiceDialog = true"
        >
          <v-icon small left>mdi-file-document-check-outline</v-icon>
          Raise Invoice
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="rw-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rw-state__text">Working out the period…</p>
    </div>

    <v-card v-else-if="!working" outlined class="rw-state rw-state--card">
      <v-icon size="44" color="grey lighten-1"
        >mdi-calculator-variant-outline</v-icon
      >
      <h3 class="rw-state__title">Pick a partner and a period</h3>
      <p class="rw-state__text">
        The working shows what each service category is charged on, and the
        collections behind it.
      </p>
    </v-card>

    <v-card
      v-else-if="!working.totals.collection_count"
      outlined
      class="rw-state rw-state--card"
    >
      <v-icon size="44" color="grey lighten-1">mdi-cash-remove</v-icon>
      <h3 class="rw-state__title">No collections in this window</h3>
      <p class="rw-state__text">
        {{ working.period.label }} has nothing to invoice for
        {{ working.partner.name }}.
      </p>
    </v-card>

    <template v-else>
      <div class="rw-buckets">
        <v-card
          v-for="bucket in working.buckets"
          :key="bucket.bucket"
          outlined
          class="rw-bucket"
        >
          <span class="rw-bucket__label">{{ bucket.label }}</span>
          <span class="rw-bucket__amount">{{
            money(bucket.gross_amount)
          }}</span>
          <span class="rw-bucket__meta">
            {{ bucket.student_count }} students ·
            {{ bucket.collection_count }} collections
          </span>
        </v-card>
      </div>

      <v-card outlined class="rw-card">
        <div class="rw-card__head">
          <div>
            <h3 class="rw-card__title">Invoice working</h3>
            <p class="rw-card__sub">
              {{ working.partner.name }} · {{ working.batch.label }} ·
              {{ working.period.label }}
            </p>
          </div>
          <v-chip x-small label outlined class="rw-chip">
            {{ working.partner.type }}
          </v-chip>
        </div>

        <div class="rw-table-wrap">
          <table class="rw-table">
            <thead>
              <tr>
                <th class="rw-th">Service Category</th>
                <th class="rw-th">Charged On</th>
                <th class="rw-th rw-th--num">Base Value</th>
                <th class="rw-th rw-th--num">Rate</th>
                <th class="rw-th rw-th--num">Gross Value</th>
                <th class="rw-th rw-th--num">GST</th>
                <th class="rw-th rw-th--num">Net Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in working.categories" :key="line.key">
                <td class="rw-td">
                  <span class="rw-cat">{{ line.name }}</span>
                  <span class="rw-hsn">HSN {{ line.hsn }}</span>
                </td>
                <td class="rw-td">
                  <span class="rw-buckets-list">
                    {{ line.buckets.join(", ") }}
                  </span>
                  <span
                    class="rw-source"
                    :class="`rw-source--${line.rate_source}`"
                  >
                    {{ sourceLabel(line.rate_source) }}
                  </span>
                </td>
                <td class="rw-td rw-td--num">
                  {{ money(line.base_amount) }}
                  <span class="rw-sub">{{ line.student_count }} students</span>
                </td>
                <td class="rw-td rw-td--num">
                  {{ number(line.rate) }}%
                  <span class="rw-sub">
                    GST {{ number(line.gst_percentage) }}%
                    {{ gstTypeLabel(line) }}
                  </span>
                </td>
                <td class="rw-td rw-td--num">{{ money(line.gross_amount) }}</td>
                <td class="rw-td rw-td--num">
                  {{ money(line.gst_amount) }}
                  <span v-if="hasGst(line)" class="rw-sub">
                    CGST {{ money(line.cgst_amount) }} · SGST
                    {{ money(line.sgst_amount) }}
                  </span>
                </td>
                <td class="rw-td rw-td--num rw-td--strong">
                  {{ money(line.net_amount) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="rw-tf" colspan="2">Total</td>
                <td class="rw-tf rw-tf--num">
                  {{ money(working.totals.collection_amount) }}
                </td>
                <td class="rw-tf rw-tf--num">
                  {{ number(working.totals.share_percentage) }}%
                </td>
                <td class="rw-tf rw-tf--num">
                  {{ money(working.totals.gross_amount) }}
                </td>
                <td class="rw-tf rw-tf--num">
                  {{ money(working.totals.gst_amount) }}
                </td>
                <td class="rw-tf rw-tf--num rw-tf--strong">
                  {{ money(working.totals.net_amount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>

      <v-card outlined class="rw-card">
        <div class="rw-card__head">
          <div>
            <h3 class="rw-card__title">Collections</h3>
            <p class="rw-card__sub">
              {{ working.totals.collection_count }} collections ·
              {{ working.totals.student_count }} students ·
              {{ money(working.totals.collection_amount) }}
            </p>
          </div>
          <v-text-field
            v-model="search"
            placeholder="Filter"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="rw-search"
          />
        </div>

        <v-data-table
          :headers="collectionHeaders"
          :items="working.collections"
          :search="search"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          dense
          class="rw-data-table"
        >
          <template #[`item.amount`]="{ item }">
            {{ money(item.amount) }}
          </template>
          <template #[`item.bucket_label`]="{ item }">
            <v-chip
              x-small
              label
              :color="bucketColor(item.bucket)"
              text-color="white"
            >
              {{ item.bucket_label }}
            </v-chip>
            <v-chip
              v-if="item.prior_period"
              x-small
              label
              outlined
              class="ml-1"
            >
              earlier
            </v-chip>
          </template>
          <template #[`item.transaction_date`]="{ item }">
            {{ date(item.transaction_date) }}
          </template>
        </v-data-table>
      </v-card>
    </template>

    <ServiceInvoiceDialog
      v-if="working"
      v-model="invoiceDialog"
      :context="invoiceContext"
      @saved="onInvoiceRaised"
    />
  </div>
</template>

<script>
import ServiceInvoiceDialog from "@/components/revenueSplit/ServiceInvoiceDialog.vue";

export default {
  components: { ServiceInvoiceDialog },

  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      partners: [],
      programBatches: [],
      working: null,
      loading: false,
      search: "",
      fromMenu: false,
      toMenu: false,
      invoiceDialog: false,
      filters: {
        partner_id: null,
        batch: null,
        from_date: this.toIso(first),
        to_date: this.toIso(last),
        include_prior_unbilled: false,
      },
      collectionHeaders: [
        { text: "USN", value: "usn_id" },
        { text: "Student", value: "student_name" },
        { text: "Sourced By", value: "partner_name" },
        { text: "Programme", value: "programme" },
        { text: "Fee Type", value: "fee_type" },
        { text: "Payment Type", value: "payment_type" },
        { text: "Receipt", value: "receipt_number" },
        { text: "Paid On", value: "transaction_date" },
        { text: "Bucket", value: "bucket_label" },
        { text: "Amount", value: "amount", align: "end" },
      ],
    };
  },

  computed: {
    batchItems() {
      const seen = new Map();

      this.programBatches.forEach((batch) => {
        const value = `${batch.year}-${batch.batch}`;
        if (seen.has(value)) return;
        seen.set(value, {
          value,
          label: `${batch.batch === 2 ? "Jul" : "Jan"} ${String(
            batch.year,
          ).slice(-2)} Batch`,
          year: batch.year,
          batch: batch.batch,
        });
      });

      return [
        { value: null, label: "All Batches" },
        ...[...seen.values()].sort(
          (a, b) => b.year - a.year || b.batch - a.batch,
        ),
      ];
    },

    selectedBatch() {
      return this.batchItems.find((item) => item.value === this.filters.batch);
    },

    invoiceContext() {
      return {
        partnerId: this.filters.partner_id,
        partnerName: this.working?.partner?.name,
        periodLabel: this.working?.period?.label,
        batchLabel: this.working?.batch?.label,
        fromDate: this.filters.from_date,
        toDate: this.filters.to_date,
        batchYear: this.selectedBatch?.year || null,
        batchNo: this.selectedBatch?.batch || null,
        includePriorUnbilled: this.filters.include_prior_unbilled,
        categories: this.working?.categories || [],
        totals: this.working?.totals,
      };
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Partner Revenue" },
      { text: "Invoice Working" },
    ]);
    this.initialise();
  },

  methods: {
    async initialise() {
      await Promise.all([this.getPartners(), this.getProgramBatches()]);
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getProgramBatches() {
      try {
        const res = await this.$axios.get("/program_batches");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    async loadWorking() {
      if (!this.filters.partner_id) return;

      this.loading = true;
      try {
        const res = await this.$axios.get("/revenue_workings", {
          params: this.queryParams(),
        });
        this.working = res.data.data;
      } catch (err) {
        this.working = null;
        this.notifyError(err, "Unable to work out this period");
      } finally {
        this.loading = false;
      }
    },

    queryParams() {
      const params = {
        partner_id: this.filters.partner_id,
        from_date: this.filters.from_date,
        to_date: this.filters.to_date,
      };

      if (this.selectedBatch?.year) {
        params.batch_year = this.selectedBatch.year;
        params.batch_no = this.selectedBatch.batch;
      }

      if (this.filters.include_prior_unbilled) {
        params.include_prior_unbilled = true;
      }

      return params;
    },

    onInvoiceRaised() {
      this.invoiceDialog = false;
      this.loadWorking();
    },

    downloadWorking() {
      const rows = [
        [
          "Service Category",
          "Charged On",
          "Base Value",
          "Rate %",
          "GST %",
          "Gross Value",
          "GST",
          "Net Value",
        ],
        ...this.working.categories.map((line) => [
          line.name,
          line.buckets.join(" / "),
          line.base_amount,
          line.rate,
          line.gst_percentage,
          line.gross_amount,
          line.gst_amount,
          line.net_amount,
        ]),
        [],
        [
          "USN",
          "Student",
          "Sourced By",
          "Programme",
          "Fee Type",
          "Payment Type",
          "Receipt",
          "Paid On",
          "Bucket",
          "Amount",
        ],
        ...this.working.collections.map((row) => [
          row.usn_id,
          row.student_name,
          row.partner_name,
          row.programme,
          row.fee_type,
          row.payment_type,
          row.receipt_number,
          row.transaction_date,
          row.bucket_label,
          row.amount,
        ]),
      ];

      const csv = rows
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      link.download = `revenue_working_${this.filters.from_date}_${this.filters.to_date}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    hasGst(line) {
      return Number(line.gst_amount) > 0;
    },

    gstTypeLabel(line) {
      if (!Number(line.gst_percentage)) return "";
      return line.gst_type === "Inclusive" ? "incl." : "excl.";
    },

    sourceLabel(source) {
      if (source === "batch") return "batch rate";
      if (source === "partner") return "partner rate";
      return "default rate";
    },

    bucketColor(bucket) {
      return (
        {
          new_admission: "deep-purple",
          first_semester: "primary",
          higher_semester: "teal",
          other_revenue: "blue-grey",
        }[bucket] || "grey"
      );
    },

    money(value) {
      const number = Number(value || 0);
      return number.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      });
    },

    date(value) {
      if (!value) return "—";
      const parsed = new Date(value);
      return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    toIso(date) {
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 10);
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rw-page {
  --rw-line: #e4e7ec;
  --rw-ink: #101828;
  --rw-ink-2: #475467;
  --rw-ink-3: #98a2b3;
  padding: 10px 14px 40px;
  margin: 0 15px;
}

.rw-toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  border-bottom: 1px solid var(--rw-line);
  margin: -10px -14px 0;
}

.rw-toolbar__inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px;
}

.rw-field {
  max-width: 170px;
  flex: 0 1 170px;
}

.rw-field--wide {
  max-width: 220px;
  flex: 0 1 220px;
}

.rw-check {
  margin: 0;
  padding: 0;
}

.rw-check ::v-deep .v-label {
  font-size: 12.5px;
}

.rw-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.rw-state--card {
  border-radius: 8px;
  background: #fff;
  margin-top: 12px;
}

.rw-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rw-ink);
}

.rw-state__text {
  margin: 0;
  font-size: 13px;
  color: var(--rw-ink-2);
}

.rw-buckets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.rw-bucket {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff;
}

.rw-bucket__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rw-ink-2);
}

.rw-bucket__amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--rw-ink);
  font-variant-numeric: tabular-nums;
}

.rw-bucket__meta {
  font-size: 11.5px;
  color: var(--rw-ink-3);
}

.rw-card {
  margin-top: 12px;
  border-radius: 8px;
  background: #fff;
}

.rw-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--rw-line);
}

.rw-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rw-ink);
}

.rw-card__sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--rw-ink-2);
}

.rw-chip {
  border-color: var(--rw-line);
  color: var(--rw-ink-2);
  font-weight: 600;
}

.rw-search {
  max-width: 220px;
}

.rw-table-wrap {
  overflow-x: auto;
}

.rw-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.rw-th {
  padding: 8px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rw-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rw-line);
  white-space: nowrap;
}

.rw-th--num {
  text-align: right;
}

.rw-td {
  padding: 10px 12px;
  font-size: 12.5px;
  color: var(--rw-ink-2);
  border-bottom: 1px solid #f4f5f7;
  vertical-align: top;
}

.rw-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rw-td--strong {
  font-weight: 700;
  color: var(--rw-ink);
}

.rw-cat {
  display: block;
  font-weight: 600;
  color: var(--rw-ink);
}

.rw-hsn,
.rw-sub {
  display: block;
  font-size: 11px;
  color: var(--rw-ink-3);
}

.rw-buckets-list {
  display: block;
}

.rw-source {
  display: inline-block;
  margin-top: 2px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #eef2f6;
  color: #344054;
}

.rw-source--default {
  background: #fef0c7;
  color: #b54708;
}

.rw-tf {
  padding: 10px 12px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--rw-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rw-line);
}

.rw-tf--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rw-tf--strong {
  font-size: 13.5px;
}

.rw-data-table ::v-deep th {
  white-space: nowrap;
}

@media (max-width: 600px) {
  .rw-page {
    margin: 0;
    padding: 10px 10px 40px;
  }

  .rw-toolbar {
    position: static;
    margin: -10px -10px 0;
  }

  .rw-field,
  .rw-field--wide {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
